/** DETALIILE ELEMENTULUI ALES pe machetă (Cătălin, 13.10.2026: «când îl
 *  selectez, să am acolo mai multe detalii despre el») — partea PURĂ.
 *
 *  Din document, nu din desen: dimensiunile scrise de motor (lățimea și
 *  înălțimea golului, înălțimea balustradei, ariile panoului), materialul și
 *  culoarea, tâmplăria și unde stă. Fiecare rând e o cheie
 *  de traducere și o valoare gata scrisă în limba ecranului; panoul
 *  (`DetaliiElement.tsx`) doar le așază. Cotele de pe machetă se măsoară pe
 *  geometria desenată (v. `plan/cote3d.ts`). */
import type { CanonicalDoc } from "../canonical";
import { fataObiectului, fisaElement, indexul, props, uPeFata } from "./selectie";

type Obj = CanonicalDoc["objects"][number];

export interface Rand {
  /** Cheia etichetei (`detalii.<cheie>`). */
  cheie: string;
  valoare: string;
  /** Mostra de culoare, lângă valoare. */
  culoare?: string;
}

export interface Grup {
  /** Cheia titlului (`detalii.grup.<cheie>`). */
  cheie: "dimensiuni" | "material" | "tamplarie" | "balcon" | "loc";
  randuri: Rand[];
}

export interface Detalii {
  eid: string;
  obiect: string;
  /** Ce e, pe înțeles (prima parte a fișei: «fereastră», «balustradă de sticlă»…). */
  titlu: string;
  /** Material și culoare, pentru capul panoului. */
  material?: string;
  culoare?: string;
  /** Etajul, pentru subtitlul panoului. */
  unde: { etaj?: number };
  grupuri: Grup[];
}

/** Numerele în limba ecranului (`ro-RO`: virgulă zecimală). */
export function formatator(bcp47: string) {
  const nf = (z: number) => new Intl.NumberFormat(bcp47, { minimumFractionDigits: z, maximumFractionDigits: z });
  const f2 = nf(2), f1 = nf(1), f0 = nf(0);
  return {
    m: (v: number) => `${f2.format(v)} m`,
    m2: (v: number) => `${f2.format(v)} m²`,
    m3: (v: number) => `${f2.format(v)} m³`,
    cm: (v: number) => `${f1.format(v * 100)} cm`,
    n: (v: number) => f0.format(v),
  };
}

const lungimeAx = (pts: number[][] | undefined) =>
  (pts ?? []).slice(1).reduce((s, p, i) => s + Math.hypot(p[0] - pts![i][0], p[1] - pts![i][1]), 0);

const aria = (poly: number[][]) =>
  Math.abs(poly.reduce((s, p, i) => {
    const q = poly[(i + 1) % poly.length];
    return s + p[0] * q[1] - q[0] * p[1];
  }, 0)) / 2;

const num = (v: unknown): number | undefined =>
  (typeof v === "number" && Number.isFinite(v) ? v : undefined);

/** Detaliile elementului `eid`, sau `null` când nu e în document. */
export function detaliiElement(doc: CanonicalDoc | null, eid: string, bcp47 = "ro-RO",
                               numeMaterial: (k: string) => string = (k) => k,
                               numeValoare: (grup: string, k: string) => string = (_g, k) => k): Detalii | null {
  if (!doc) return null;
  const fisa = fisaElement(doc, eid);
  const ix = indexul(doc);
  const o = ix.byid.get(fisa?.parte?.din ?? eid);
  if (!fisa || !o) return null;
  const F = formatator(bcp47);
  const g = (o.geometry ?? {}) as Record<string, unknown>;
  const pr = o.properties as Record<string, unknown>;
  const dim: Rand[] = [], mat: Rand[] = [], tam: Rand[] = [], bal: Rand[] = [];
  const pune = (l: Rand[], cheie: string, v: string | undefined, culoare?: string) => {
    if (v !== undefined && v !== "") l.push({ cheie, valoare: v, ...(culoare ? { culoare } : {}) });
  };
  const material = typeof pr.material === "string" ? pr.material : undefined;
  let culoare = typeof pr.colour === "string" ? pr.colour : undefined;

  let titlu = fisa.ce.split(" — ")[0].split(", ")[0];
  const parte = fisa.parte;
  if (parte?.fel === "placa" && parte.placa) {
    // O PLACĂ A FAȚADEI VENTILATE: dimensiunile ei (fără rost), rostul, al
    // câtelea e, culoarea ei (a regulii `tile`, când are una)
    const pl = parte.placa;
    titlu = "placă de fațadă ventilată";
    pune(dim, "placa_nr", `${F.n(pl.k + 1)} / ${F.n(pl.din)}`);
    pune(dim, "latime", F.m(pl.latime));
    pune(dim, "inaltime", F.m(pl.inaltime));
    pune(dim, "grosime", num(g.thickness_m) !== undefined ? F.cm(num(g.thickness_m)!) : undefined);
    pune(dim, "rost", F.cm(pl.rost));
    const jt = ((props(o, "architecture.panel").joints ?? {}) as { module_m?: number[] });
    if (jt.module_m?.length === 2) pune(dim, "format", `${F.m(jt.module_m[0]).replace(" m", "")} × ${F.m(jt.module_m[1])}`);
    pune(dim, "suprafata", F.m2(pl.latime * pl.inaltime));
    if (pl.culoare) culoare = pl.culoare;
  } else if (parte?.fel === "mana" && parte.mana) {
    // MÂNA CURENTĂ: lungimea ei (a balustradei), secțiunea, profilul
    const m = parte.mana;
    titlu = "mână curentă";
    const ax = g.axis as number[][] | undefined;
    pune(dim, "lungime", ax?.length ? F.m(lungimeAx(ax)) : undefined);
    pune(dim, "sectiune", m.profile === "round" ? `⌀ ${F.cm(m.width_m)}` : `${F.cm(m.width_m)} × ${F.cm(m.height_m)}`);
    pune(mat, "profil_mana", numeValoare("profil", m.profile));
    culoare = m.colour;
    pune(mat, "material", m.material ? numeMaterial(m.material) : undefined);
    if (m.colour) pune(mat, "culoare", m.colour.toUpperCase(), m.colour);
  } else if (parte?.fel === "profil" && parte.profil) {
    // PROFILUL DE STICLĂ: lungimea lui (a balustradei) și secțiunea șinei
    const p = parte.profil;
    titlu = "profil de sticlă";
    const ax = g.axis as number[][] | undefined;
    pune(dim, "lungime", ax?.length ? F.m(lungimeAx(ax)) : undefined);
    pune(dim, "sectiune", `${F.cm(p.width_m)} × ${F.cm(p.height_m)}`);
    culoare = p.colour;
    pune(mat, "material", numeMaterial(p.material));
    if (p.colour) pune(mat, "culoare", p.colour.toUpperCase(), p.colour);
  } else if (o.type === "Window" || o.type === "Door") {
    const w = num(g.width_m), h = num(g.height_m), sill = num(g.sill_m);
    pune(dim, "latime", w !== undefined ? F.m(w) : undefined);
    pune(dim, "inaltime", h !== undefined ? F.m(h) : undefined);
    if (o.type === "Window") pune(dim, "parapet", sill !== undefined ? F.m(sill) : undefined);
    pune(dim, "suprafata", w !== undefined && h !== undefined ? F.m2(w * h) : undefined);
    const op = props(o, "architecture.opening");
    const j = (op.joinery ?? {}) as Record<string, unknown>;
    if (typeof j.colour === "string") culoare = j.colour;
    pune(tam, "rama", typeof j.frame_material === "string" ? numeMaterial(j.frame_material) : undefined);
    pune(tam, "culoare_toc", typeof j.colour === "string" ? j.colour.toUpperCase() : undefined,
         typeof j.colour === "string" ? j.colour : undefined);
    pune(tam, "canate", num(j.leaves) !== undefined ? F.n(num(j.leaves)!) : undefined);
    const moduri = Array.isArray(j.leaf_modes) && j.leaf_modes.length ? j.leaf_modes as string[]
      : typeof j.opening === "string" ? [j.opening] : [];
    pune(tam, "deschidere", moduri.length
      ? [...new Set(moduri)].map((m) => numeValoare("deschidere", m)).join(", ") : undefined);
    pune(tam, "vitraj", typeof j.glazing === "string" ? numeValoare("vitraj", j.glazing) : undefined);
    pune(tam, "profil", num(j.frame_profile_m) !== undefined ? F.cm(num(j.frame_profile_m)!) : undefined);
    pune(tam, "adancime_toc", num(j.frame_depth_m) !== undefined ? F.cm(num(j.frame_depth_m)!) : undefined);
    const prag = (j.threshold ?? {}) as Record<string, unknown>;
    pune(tam, "prag", typeof prag.material === "string" ? numeMaterial(prag.material) : undefined);
    if (typeof op.model === "string") pune(tam, "model", numeValoare("model", op.model));
  } else if (o.type === "Railing") {
    const ax = g.axis as number[][] | undefined;
    pune(dim, "lungime", ax?.length ? F.m(lungimeAx(ax)) : undefined);
    pune(dim, "inaltime", num(g.height_m) !== undefined ? F.m(num(g.height_m)!) : undefined);
    pune(dim, "grosime", num(g.thickness_m) !== undefined ? F.cm(num(g.thickness_m)!) : undefined);
    pune(mat, "fel", fisa.fel ? numeValoare("fel", fisa.fel) : undefined);
    const rl = props(o, "architecture.railing");
    pune(mat, "pe", typeof rl.outdoor === "string" ? numeValoare("pe", rl.outdoor) : undefined);
    pune(mat, "plantare", typeof rl.planting === "string" ? numeValoare("plantare", rl.planting) : undefined);
  } else if (o.type === "FacadePanel") {
    const pan = props(o, "architecture.panel");
    const lung = num(pan.length_m) ?? (num(pan.u1) !== undefined && num(pan.u0) !== undefined && num(pan.u1)! > num(pan.u0)!
      ? num(pan.u1)! - num(pan.u0)! : (g.axis ? lungimeAx(g.axis as number[][]) : undefined));
    pune(dim, "lungime", lung !== undefined && lung > 0 ? F.m(lung) : undefined);
    pune(dim, "inaltime", num(g.height_m) !== undefined ? F.m(num(g.height_m)!) : undefined);
    pune(dim, "grosime", num(g.thickness_m) !== undefined ? F.cm(num(g.thickness_m)!) : undefined);
    pune(dim, "cota_jos", num(g.sill_m) !== undefined ? F.m(num(g.sill_m)!) : undefined);
    pune(dim, "arie_bruta", num(pan.gross_m2) !== undefined ? F.m2(num(pan.gross_m2)!) : undefined);
    if ((num(pan.openings_m2) ?? 0) > 0) pune(dim, "arie_goluri", F.m2(num(pan.openings_m2)!));
    pune(dim, "arie_neta", num(pan.net_m2) !== undefined ? F.m2(num(pan.net_m2)!) : undefined);
    if ((num(pan.volume_m3) ?? 0) > 0) pune(dim, "volum", F.m3(num(pan.volume_m3)!));
    pune(mat, "fel", fisa.fel ? numeValoare("fel", fisa.fel) : undefined);
    pune(mat, "strat", typeof pr.layer === "string" ? numeValoare("strat", pr.layer) : undefined);
    pune(mat, "unde", typeof pr.surface === "string" ? numeValoare("unde", pr.surface) : undefined);
    const jt = (pan.joints ?? {}) as Record<string, unknown>;
    const placi = num(jt.placi) ?? (Array.isArray(jt.tiles) ? jt.tiles.length : undefined);
    pune(mat, "placi", placi ? F.n(placi) : undefined);
    const mod = Array.isArray(jt.module_m) ? (jt.module_m as number[]) : undefined;
    pune(mat, "format", mod?.length === 2 ? `${F.m(mod[0]).replace(" m", "")} × ${F.m(mod[1])}` : undefined);
    pune(mat, "rost", num(jt.width_m) !== undefined ? F.cm(num(jt.width_m)!) : undefined);
    const c = (pan.curtain ?? null) as Record<string, unknown> | null;
    if (c && typeof c === "object") {
      // PERETELE CORTINĂ (§29): sistemul, grila și cât din el e sticlă — tâmplăria
      // lui, nu un placaj
      titlu = "perete cortină";
      pune(dim, "adancime", num(g.depth_m) !== undefined ? F.cm(num(g.depth_m)!) : undefined);
      const mont = Array.isArray(c.mullions_u) ? (c.mullions_u as unknown[]).map(Number).filter(Number.isFinite) : [];
      const pasi = mont.slice(1).map((u, i) => u - mont[i]).filter((d) => d > 0.3);
      pune(tam, "sistem", typeof c.system === "string" ? c.system : undefined);
      pune(tam, "varianta", typeof c.variant === "string" ? numeValoare("varianta_cortina", c.variant) : undefined);
      pune(tam, "montanti", mont.length ? F.n(mont.length) : undefined);
      pune(tam, "modul", pasi.length ? F.m(Math.max(...pasi)) : undefined);
      const pr_ = (c.profile ?? {}) as Record<string, unknown>;
      if (num(pr_.width_m) !== undefined && num(pr_.mullion_m) !== undefined) {
        pune(tam, "profil", `${F.cm(num(pr_.width_m)!)} × ${F.cm(num(pr_.mullion_m)!)}`);
      }
      const gl = (c.glass ?? {}) as Record<string, unknown>;
      pune(tam, "vitraj", num(gl.thickness_m) !== undefined ? F.cm(num(gl.thickness_m)!) : undefined);
      if (typeof gl.colour === "string") pune(tam, "culoare_sticla", gl.colour.toUpperCase(), gl.colour);
      const op = (c.opaque ?? {}) as Record<string, unknown>;
      if (typeof op.colour === "string") pune(tam, "culoare_opac", op.colour.toUpperCase(), op.colour);
      if (num(c.vision_m2) !== undefined) pune(dim, "arie_vitrata", F.m2(num(c.vision_m2)!));
      if (num(c.opaque_m2) !== undefined) pune(dim, "arie_opaca", F.m2(num(c.opaque_m2)!));
      if ((num(c.vents) ?? 0) > 0) pune(tam, "ferestre_cortina", F.n(num(c.vents)!));
      if (Array.isArray(c.doors) && c.doors.length) pune(tam, "usi", F.n(c.doors.length));
      if (c.parapet === true) pune(tam, "peste_placa", numeValoare("da", "da"));
    }
  } else if (o.type === "Space") {
    const poly = (g.poly as number[][] | undefined) ?? [];
    if (poly.length >= 3) {
      pune(dim, "arie", F.m2(aria(poly)));
      // lățimea în lungul feței și adâncimea spre afară, pe fața balconului
      const f = fataObiectului(ix, o);
      if (f) {
        const us = poly.map((p) => uPeFata(f, p[0], p[1]));
        const u = us.map((x) => x.u), d = us.map((x) => x.d);
        pune(dim, "latime", F.m(Math.max(...u) - Math.min(...u)));
        pune(dim, "adancime", F.m(Math.max(...d) - Math.min(...d)));
      }
    }
    const sp = props(o, "architecture.space");
    pune(bal, "inchidere", typeof sp.inchidere === "string" ? numeValoare("inchidere", sp.inchidere) : undefined);
    pune(bal, "pe_colt", sp.corner === "wrap" ? numeValoare("da", "da") : undefined);
    pune(bal, "varianta", typeof sp.varianta === "string" ? sp.varianta : undefined);
    pune(bal, "compozitie", typeof sp.compozitie === "string" ? sp.compozitie : undefined);
    // ce stă pe el: balustradele și despărțitoarele de pe aceeași placă
    const st = String(pr.storey ?? "");
    const pePlaca = (doc.objects ?? []).filter((x) => x.type === "Railing" && String(x.properties.storey ?? "") === st
      && pePoligon(((x.geometry ?? {}) as { axis?: number[][] }).axis, poly));
    if (pePlaca.length) {
      pune(bal, "balustrade", [...new Set(pePlaca.map((x) =>
        `${numeValoare("fel", String(x.properties.kind ?? ""))}${x.properties.material ? ` (${numeMaterial(String(x.properties.material))})` : ""}`))].join(", "));
    }
  }
  if (parte?.fel !== "mana" && parte?.fel !== "profil") {
    if (material) pune(mat, "material", numeMaterial(material));
    // la goluri, culoarea e a tocului și stă la tâmplărie
    if (culoare && o.type !== "Window" && o.type !== "Door") pune(mat, "culoare", culoare.toUpperCase(), culoare);
  }

  // UNDE STĂ: corpul, etajul, coloana de apartamente. Fața (codul ei,
  // «r0hi») și regula de stil nu spun nimic omului — costul le-a luat locul
  // (`DetaliiElement`, v. `costElement.ts`)
  const loc: Rand[] = [];
  pune(loc, "corp", fisa.corp);
  pune(loc, "etaj", fisa.etaj === undefined ? undefined : fisa.etaj === 0 ? numeValoare("etaj", "parter") : F.n(fisa.etaj));
  pune(loc, "coloana", fisa.coloana !== undefined ? F.n(fisa.coloana) : undefined);
  const grupuri: Grup[] = ([["dimensiuni", dim], ["material", mat], ["tamplarie", tam], ["balcon", bal],
                             ["loc", loc]] as [Grup["cheie"], Rand[]][])
    .filter(([, r]) => r.length).map(([cheie, randuri]) => ({ cheie, randuri }));
  const matCap = parte?.fel === "mana" ? parte.mana?.material
    : parte?.fel === "profil" ? parte.profil?.material : material;
  return { eid, obiect: o.type, titlu,
           ...(matCap ? { material: numeMaterial(matCap) } : {}), ...(culoare ? { culoare } : {}),
           unde: { etaj: fisa.etaj }, grupuri };
}

/** Cifrele COTELOR, din document, când le are: lățimea și înălțimea golului
 *  (tâmplăria desenată e cu câțiva centimetri mai mică decât golul), înălțimea
 *  balustradei și a panoului, lungimea panoului pe față. Ce lipsește rămâne
 *  măsurat pe desen — așa cota și panoul spun același număr. */
export function dimensiuniCote(doc: CanonicalDoc | null, eid: string):
    { lungime?: number; inaltime?: number; adancime?: number } {
  // o parte (placa, mâna curentă) se măsoară pe desen: e exact cât e desenată
  if (eid.includes("#")) return {};
  const o = doc ? indexul(doc).byid.get(eid) : undefined;
  if (!o) return {};
  const g = (o.geometry ?? {}) as Record<string, unknown>;
  if (o.type === "Window" || o.type === "Door") {
    return { ...(num(g.width_m) ? { lungime: num(g.width_m) } : {}), ...(num(g.height_m) ? { inaltime: num(g.height_m) } : {}) };
  }
  if (o.type === "Railing") return num(g.height_m) ? { inaltime: num(g.height_m) } : {};
  if (o.type === "FacadePanel") {
    const pan = props(o, "architecture.panel");
    const u0 = num(pan.u0), u1 = num(pan.u1);
    return { ...(num(g.height_m) ? { inaltime: num(g.height_m) } : {}),
             ...(u0 !== undefined && u1 !== undefined && u1 - u0 > 0.01 && g.axis ? { lungime: u1 - u0 } : {}) };
  }
  return {};
}

/** Axa balustradei stă pe placa balconului (mijlocul ei la sub 0,3 m de contur
 *  sau înăuntru). */
function pePoligon(ax: number[][] | undefined, poly: number[][]): boolean {
  if (!ax?.length) return false;
  const m = ax[Math.floor(ax.length / 2)];
  const p = ax.length % 2 ? m : [(ax[ax.length / 2 - 1][0] + m[0]) / 2, (ax[ax.length / 2 - 1][1] + m[1]) / 2];
  let inauntru = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = poly[i], [xj, yj] = poly[j];
    if ((yi > p[1]) !== (yj > p[1]) && p[0] < ((xj - xi) * (p[1] - yi)) / (yj - yi) + xi) inauntru = !inauntru;
  }
  if (inauntru) return true;
  for (let i = 0; i < poly.length; i++) {
    const a = poly[i], b = poly[(i + 1) % poly.length];
    const dx = b[0] - a[0], dy = b[1] - a[1], L2 = dx * dx + dy * dy || 1;
    const t = Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / L2));
    if (Math.hypot(p[0] - a[0] - t * dx, p[1] - a[1] - t * dy) < 0.3) return true;
  }
  return false;
}

export type { Obj };
